import {gql} from '@apollo/client';
import {useCallback, useRef} from 'react';

import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';

import {
  RoutineExecutionsQuery,
  RoutineExecutionsQueryVariables,
  RoutineExecutionsQuery_routineExecutions_RoutineExecutionListingNode_items_RoutineExecutionNode as RoutineExecutionItem,
} from './__generated/RoutineExecutionsQuery';

export const ROUTINE_EXECUTIONS_QUERY = gql`
  query RoutineExecutionsQuery($routineId: StrictString!, $first: StrictInt, $after: StrictString)
  @engComponent(name: LLM)
  @skipAudit {
    routineExecutions(routineId: $routineId, first: $first, after: $after) {
      nextToken
      total
      items {
        id
        status
        chatSession {
          id
          name
        }
        createdAt
      }
    }
  }
`;

export type RoutineExecution = RoutineExecutionItem;

const DEFAULT_PAGE_SIZE = 15;

const EMPTY_RUNS: ReadonlyArray<RoutineExecutionItem> = [];

interface UseRoutineExecutionsResult {
  runs: ReadonlyArray<RoutineExecutionItem>;
  total: number;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Fetches a routine's past executions (its "runs"), newest first, with cursor-based "Load more"
 * paging: each "Load more" fetches the next page via the `after` cursor and appends it to the
 * already-loaded list. Skipped until a routineId is provided and the caller enables it.
 */
export default function useRoutineExecutions({
  routineId,
  enabled = true,
}: {
  routineId: string | null;
  enabled?: boolean;
}): UseRoutineExecutionsResult {
  // Guards loadMore against overlapping fetches (a fast double-click before the page resolves).
  const isFetchingMoreRef = useRef(false);

  const queryResult = useSkippableBenchlingQuery<RoutineExecutionsQuery, RoutineExecutionsQueryVariables>(
    ROUTINE_EXECUTIONS_QUERY,
    {
      skip: !enabled || routineId == null,
      variables: {
        routineId: routineId ?? '',
        first: DEFAULT_PAGE_SIZE,
      },
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
    }
  );

  const data = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;
  const runs = data?.routineExecutions.items ?? EMPTY_RUNS;
  const total = data?.routineExecutions.total ?? 0;
  const nextToken = data?.routineExecutions.nextToken ?? '';
  // The server returns an empty cursor once there are no more pages.
  const hasMore = nextToken !== '';

  const isLoading = enabled && routineId != null ? queryResult.type === QueryResultType.PENDING : false;
  const isError = queryResult.type === QueryResultType.ERROR;

  const {fetchMore} = queryResult;

  const loadMore = useCallback(() => {
    if (routineId == null || nextToken === '' || isFetchingMoreRef.current) {
      return;
    }
    isFetchingMoreRef.current = true;
    fetchMore({
      variables: {
        routineId,
        first: DEFAULT_PAGE_SIZE,
        after: nextToken,
      },
      // Append the next page's items to the ones already loaded and adopt its cursor, rather than
      // replacing the accumulated list. (No field policy is configured for this query, so the merge
      // lives here.)
      updateQuery: (previous, {fetchMoreResult}) => {
        if (fetchMoreResult == null) {
          return previous;
        }
        return {
          ...fetchMoreResult,
          routineExecutions: {
            ...fetchMoreResult.routineExecutions,
            // A cursor page reports the number of executions from that cursor onward. Keep the
            // first page's all-time count for the Agent overview.
            total: previous.routineExecutions.total,
            items: [...previous.routineExecutions.items, ...fetchMoreResult.routineExecutions.items],
          },
        };
      },
    })
      .catch(() => {
        // A failed page surfaces through the query result's error state on the next render; nothing
        // to do here beyond releasing the in-flight guard below.
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [fetchMore, nextToken, routineId]);

  return {
    runs,
    total,
    isLoading,
    isError,
    hasMore,
    loadMore,
  };
}
